import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const SoilProperties: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeScience/SoilProperties.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeScience/SoilProperties.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Soil – Class 3 Science | Types, Layers & Importance | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Learn about soil, its types, layers, and importance in Class 3 Science. Explore how soil supports plants, holds water, and helps living things survive at Mama’s Guidance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>🌍 Chapter 9: Soil</h1>

                            <div className="section">
                                <h2>🌱 What is Soil?</h2>
                                <p>Soil is the top layer of the Earth where plants grow. It is made up of tiny rocks, minerals, air, water, and dead plants and animals. It takes thousands of years to form soil from rocks!</p>
                                <div className="fun-fact">🧠 <strong>Fun Fact:</strong> Soil is also called &quot;earth&quot; or &quot;dirt.&quot;</div>
                            </div>

                            <div className="section">
                                <h2>📚 How is Soil Formed?</h2>
                                <p>Soil is formed when rocks break down into smaller pieces due to wind, water, heat, and cold. This process is called <strong>weathering</strong>. Dead plants and animals mix with the rock pieces to make soil rich in nutrients.</p>
                            </div>

                            <div className="section">
                                <h2>🧱 Layers of Soil</h2>
                                <ul>
                                    <li><strong>Topsoil</strong> – The top layer. It is dark, soft, and rich in nutrients. Plants grow here.</li>
                                    <li><strong>Subsoil</strong> – The middle layer. It has less nutrients and more stones.</li>
                                    <li><strong>Bedrock</strong> – The bottom layer. It is made of solid rock.</li>
                                </ul>
                                <p>🌿 <strong>Topsoil</strong> is the most useful layer for farmers and gardeners.</p>
                            </div>

                            <div className="section">
                                <h2>🌾 Types of Soil</h2>
                                <h3>1. Clayey Soil</h3>
                                <ul>
                                    <li>Made of very fine particles</li>
                                    <li>Holds a lot of water</li>
                                    <li>Feels smooth and sticky</li>
                                    <li>Good for making pots and toys</li>
                                </ul>
                                <p>🌱 Not the best for growing most plants because it holds too much water.</p>

                                <h3>2. Sandy Soil</h3>
                                <ul>
                                    <li>Made of large, loose grains</li>
                                    <li>Dries quickly and doesn’t hold water well</li>
                                    <li>Found in deserts</li>
                                    <li>Not good for farming</li>
                                </ul>

                                <h3>3. Loamy Soil</h3>
                                <ul>
                                    <li>A mix of clay, sand, and humus</li>
                                    <li>Best soil for growing plants</li>
                                    <li>Rich in nutrients and holds water well</li>
                                    <li>Used in gardens and farms</li>
                                </ul>
                            </div>

                            <div className="section">
                                <h2>🌼 Importance of Soil</h2>
                                <ul>
                                    <li>Plants grow in soil</li>
                                    <li>Provides food and shelter to animals like earthworms, ants, and rabbits</li>
                                    <li>Used to grow fruits, vegetables, and crops</li>
                                    <li>Used to make bricks, pots, and tiles</li>
                                </ul>
                            </div>

                            <div className="section">
                                <h2>🛡️ How to Protect Soil? (Soil Conservation)</h2>
                                <ul>
                                    <li>Grow more trees – Roots hold soil in place</li>
                                    <li>Avoid cutting trees (deforestation)</li>
                                    <li>Do not overuse chemicals on soil</li>
                                    <li>Prevent soil erosion by planting grass and plants</li>
                                    <li>Do not throw waste or plastics in soil</li>
                                </ul>
                            </div>

                            <div className="section">
                                <h2>✅ Quick Recap</h2>
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Topic</th>
                                            <th>Key Points</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>What is soil?</td>
                                            <td>Top layer of Earth, helps plants grow</td>
                                        </tr>
                                        <tr>
                                            <td>Soil Formation</td>
                                            <td>Formed from rocks and dead plants over years</td>
                                        </tr>
                                        <tr>
                                            <td>Soil Layers</td>
                                            <td>Topsoil, Subsoil, Bedrock</td>
                                        </tr>
                                        <tr>
                                            <td>Soil Types</td>
                                            <td>Clayey, Sandy, Loamy</td>
                                        </tr>
                                        <tr>
                                            <td>Importance</td>
                                            <td>Grows plants, supports animals, used in building</td>
                                        </tr>
                                        <tr>
                                            <td>Protection</td>
                                            <td>Plant trees, stop erosion, avoid waste dumping</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>🌍 Class 3 Science – Chapter 9: Soil</h1>

                        <div className="section">
                            <h2>🌱 What is Soil?</h2>
                            <p>Soil is the top layer of the Earth where plants grow. It is made up of tiny rocks, minerals, air, water, and dead plants and animals. It takes thousands of years to form soil from rocks!</p>
                            <div className="fun-fact">🧠 <strong>Fun Fact:</strong> Soil is also called &quot;earth&quot; or &quot;dirt.&quot;</div>
                        </div>

                        <div className="section">
                            <h2>📚 How is Soil Formed?</h2>
                            <p>Soil is formed when rocks break down into smaller pieces due to wind, water, heat, and cold. This process is called <strong>weathering</strong>. Dead plants and animals mix with the rock pieces to make soil rich in nutrients.</p>
                        </div>

                        <div className="section">
                            <h2>🧱 Layers of Soil</h2>
                            <ul>
                                <li><strong>Topsoil</strong> – The top layer. It is dark, soft, and rich in nutrients. Plants grow here.</li>
                                <li><strong>Subsoil</strong> – The middle layer. It has less nutrients and more stones.</li>
                                <li><strong>Bedrock</strong> – The bottom layer. It is made of solid rock.</li>
                            </ul>
                            <p>🌿 <strong>Topsoil</strong> is the most useful layer for farmers and gardeners.</p>
                        </div>

                        <div className="section">
                            <h2>🌾 Types of Soil</h2>
                            <h3>1. Clayey Soil</h3>
                            <ul>
                                <li>Made of very fine particles</li>
                                <li>Holds a lot of water</li>
                                <li>Feels smooth and sticky</li>
                                <li>Good for making pots and toys</li>
                            </ul>
                            <p>🌱 Not the best for growing most plants because it holds too much water.</p>

                            <h3>2. Sandy Soil</h3>
                            <ul>
                                <li>Made of large, loose grains</li>
                                <li>Dries quickly and doesn’t hold water well</li>
                                <li>Found in deserts</li>
                                <li>Not good for farming</li>
                            </ul>

                            <h3>3. Loamy Soil</h3>
                            <ul>
                                <li>A mix of clay, sand, and humus</li>
                                <li>Best soil for growing plants</li>
                                <li>Rich in nutrients and holds water well</li>
                                <li>Used in gardens and farms</li>
                            </ul>
                        </div>

                        <div className="section">
                            <h2>🌼 Importance of Soil</h2>
                            <ul>
                                <li>Plants grow in soil</li>
                                <li>Provides food and shelter to animals like earthworms, ants, and rabbits</li>
                                <li>Used to grow fruits, vegetables, and crops</li>
                                <li>Used to make bricks, pots, and tiles</li>
                            </ul>
                        </div>

                        <div className="section">
                            <h2>🛡️ How to Protect Soil? (Soil Conservation)</h2>
                            <ul>
                                <li>Grow more trees – Roots hold soil in place</li>
                                <li>Avoid cutting trees (deforestation)</li>
                                <li>Do not overuse chemicals on soil</li>
                                <li>Prevent soil erosion by planting grass and plants</li>
                                <li>Do not throw waste or plastics in soil</li>
                            </ul>
                        </div>

                        <div className="section">
                            <h2>✅ Quick Recap</h2>
                            <table>
                                <tr>
                                    <th>Topic</th>
                                    <th>Key Points</th>
                                </tr>
                                <tr>
                                    <td>What is soil?</td>
                                    <td>Top layer of Earth, helps plants grow</td>
                                </tr>
                                <tr>
                                    <td>Soil Formation</td>
                                    <td>Formed from rocks and dead plants over years</td>
                                </tr>
                                <tr>
                                    <td>Soil Layers</td>
                                    <td>Topsoil, Subsoil, Bedrock</td>
                                </tr>
                                <tr>
                                    <td>Soil Types</td>
                                    <td>Clayey, Sandy, Loamy</td>
                                </tr>
                                <tr>
                                    <td>Importance</td>
                                    <td>Grows plants, supports animals, used in building</td>
                                </tr>
                                <tr>
                                    <td>Protection</td>
                                    <td>Plant trees, stop erosion, avoid waste dumping</td>
                                </tr>
                            </table>
                        </div>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default SoilProperties;
